import { EDatasourceType, IActionDefinitionDto, IDatasourceDto } from "@kuika/kuika-cl-model";
import * as React from "react";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaEntitySwitcher, {
  KuikaEntitySwitcherItem
} from "../../../ui-kit/kuika-entity-switcher/kuika-entity-switcher";
import { canManageActions, deleteActionFromRow, hasActionRowMenu } from "../datasource-row-actions";
import { getDatasourceDisplayName } from "../datasources-landing-model";
import { getEditorGlyph, IEditorGlyph } from "./editor-glyph";
import { EditorGlyphIcon } from "./editor-glyph-icon";
import { buildActionFolders, buildActionSwitcherItemsOfType } from "./editor-switcher-items";
import { ISwitcherDelete, useSwitcherDelete } from "./use-switcher-delete";

/**
 * The identity chip on a SAP action editor's bar: which action is open, and the way to the
 * other ones on the same datasource.
 *
 * Like every other switcher here, a pick is PARKED as the editor's pending close rather
 * than opened outright, so the editor's close path decides what happens to unsaved work.
 */

export interface ISapActionSwitcherChipProps {
  sd: IScreenDesignContextState;
  setNextCallbackFunction: (callback?: () => void) => void;
  datasource: IDatasourceDto;
  currentActionId: string | undefined;
  currentName: string;
  /** The editor's guarded close — it leads to the datasources list. */
  onManageAll: () => void;
}

export const SapActionSwitcherChip = ({
  sd,
  setNextCallbackFunction,
  datasource,
  currentActionId,
  currentName,
  onManageAll
}: ISapActionSwitcherChipProps): React.ReactElement => {
  const { deleteOption, deleteModal }: ISwitcherDelete<IActionDefinitionDto> = useSwitcherDelete(
    "action",
    (action: IActionDefinitionDto): void => void deleteActionFromRow(sd, action)
  );
  // An action's `actionType` is drawn from the same enum as a datasource's type — that is
  // how the canvas picks this editor (`editingAction.actionType === EDatasourceType.SAP`).
  const items: KuikaEntitySwitcherItem[] = buildActionSwitcherItemsOfType(
    sd.actionDefinitions ?? [],
    datasource?.id,
    EDatasourceType.SAP
  );

  // The datasources list's own rule for an action row's Delete.
  const canDeleteAction: boolean = canManageActions(sd) && !!datasource && hasActionRowMenu(datasource);
  const glyph: IEditorGlyph = getEditorGlyph({
    kind: "action",
    actionKind: "sap",
    datasourceName: datasource ? getDatasourceDisplayName(datasource) : undefined
  });

  const open = (action: IActionDefinitionDto | undefined, isCreating: boolean, name?: string): void => {
    setNextCallbackFunction((): void =>
      sd.setEditingActionsDatasource(datasource, action, isCreating, false, undefined, name)
    );
  };

  return (
    <>
      <KuikaEntitySwitcher
        trigger="chip"
        triggerIcon={<EditorGlyphIcon glyph={glyph} />}
        triggerIconTooltip={glyph.label}
        triggerLabel={currentName}
        triggerTestId="sapActionSwitcherTrigger"
        items={items}
        current={currentActionId ? { id: currentActionId, name: currentName } : undefined}
        entityLabel="SAP Action"
        iconName="sap"
        iconType="datasource-icons"
        onSelect={(item: KuikaEntitySwitcherItem): void => {
          const action: IActionDefinitionDto | undefined = (sd.actionDefinitions ?? []).find(
            (candidate: IActionDefinitionDto): boolean => candidate.id === item.id
          );
          if (!action) return;
          open(action, false);
        }}
        onCreateNew={canManageActions(sd) ? (): void => open(undefined, true, "SapAction") : undefined}
        folders={buildActionFolders(sd.project?.actionFolders, datasource?.id)}
        onManageAll={onManageAll}
        getRowActions={(item: KuikaEntitySwitcherItem): KuikaDropdownMenuItemType[] => {
          const action: IActionDefinitionDto | undefined = (sd.actionDefinitions ?? []).find(
            (candidate: IActionDefinitionDto): boolean => candidate.id === item.id
          );
          if (!action) return [];
          return [deleteOption(action, !canDeleteAction)];
        }}
      />
      {deleteModal}
    </>
  );
};
